import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Button,
  Icon,
  Center,
  Divider,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

// -------- HERO SECTION --------
export const CollegeProjectHero = ({ data, onEnrollClick }) => {
  return (
    <Box
      py={{ base: 20, md: 28 }}
      bgGradient="linear(to-br, brand.50, brand.100)"
      position="relative"
      overflow="hidden"
    >
      <Container maxW="container.xl">
        <VStack spacing={8} textAlign="center" zIndex={2} position="relative">
          <Badge
            bg="brand.500"
            color="white"
            px={4}
            py={2}
            borderRadius="full"
            fontSize="sm"
            fontWeight="600"
          >
            {data.badge}
          </Badge>

          <Heading
            as="h1"
            size="2xl"
            fontWeight="800"
            lineHeight="1.2"
            maxW="3xl"
            color="brand.900"
          >
            {data.title}
          </Heading>

          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color="gray.700"
            maxW="2xl"
            fontWeight="500"
          >
            {data.description}
          </Text>

          <HStack
            spacing={{ base: 4, md: 8 }}
            justify="center"
            flexWrap="wrap"
            pt={4}
          >
            {data.trustElements?.map((element, idx) => (
              <HStack key={idx} spacing={2} fontSize="sm" fontWeight="600">
                <Icon as={element.icon} color="brand.500" boxSize={5} />
                <Text>{element.text}</Text>
              </HStack>
            ))}
          </HStack>

          <Button
            as="a"
            href={data.ctaButton?.href}
            size="lg"
            colorScheme="brand"
            fontWeight="700"
            px={8}
            py={6}
            mt={6}
            onClick={onEnrollClick}
            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
            transition="all 0.3s ease"
          >
            {data.ctaButton?.text}
          </Button>
        </VStack>
      </Container>
    </Box>
  );
};

// -------- BENEFITS SECTION --------
export const CollegeProjectBenefits = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.xl">
        <VStack spacing={12} mb={12} textAlign="center">
          <VStack spacing={4}>
            <Heading as="h2" size="xl" fontWeight="800" color="brand.900">
              {data.title}
            </Heading>
            <Text fontSize="lg" color="gray.700" maxW="3xl">
              {data.description}
            </Text>
          </VStack>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {data.benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <Box
                p={6}
                borderRadius="lg"
                border="2px solid"
                borderColor="brand.100"
                bg="brand.50"
                _hover={{
                  borderColor: 'brand.500',
                  bg: 'brand.100',
                  transform: 'translateY(-4px)',
                }}
                transition="all 0.3s ease"
                cursor="pointer"
              >
                <VStack spacing={4} align="start">
                  <Icon
                    as={benefit.icon}
                    boxSize={8}
                    color="brand.500"
                  />
                  <VStack spacing={2} align="start" w="100%">
                    <Heading as="h3" size="md" fontWeight="700" color="brand.900">
                      {benefit.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                      {benefit.description}
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            </motion.div>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

// -------- ACADEMIC PROJECT PROPOSAL SECTION --------
export const CollegeProjectAcademicProposal = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="brand.50">
      <Container maxW="container.xl">
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center" mb={6}>
            <Heading as="h2" size="xl" fontWeight="800" color="brand.900">
              {data.title}
            </Heading>
            <Text fontSize="lg" color="gray.700" maxW="3xl">
              {data.description}
            </Text>
          </VStack>

          <Box
            bg="white"
            borderRadius="xl"
            p={{ base: 6, md: 8 }}
            border="2px solid"
            borderColor="brand.200"
            w="100%"
          >
            <VStack spacing={8} align="start">
              <VStack spacing={2} align="start" w="100%">
                <Badge colorScheme="blue" mb={2}>
                  Capstone Project
                </Badge>
                <Heading as="h3" size="lg" fontWeight="800" color="brand.900">
                  {data.projectTitle}
                </Heading>
              </VStack>

              <Text fontSize="md" color="gray.700" lineHeight="1.8">
                {data.overview}
              </Text>

              <Divider />

              <Box w="100%">
                <Heading
                  as="h4"
                  size="md"
                  fontWeight="700"
                  color="brand.900"
                  mb={4}
                >
                  Key Features
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {data.features.map((feature, idx) => (
                    <Box
                      key={idx}
                      p={4}
                      bg="brand.50"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="brand.100"
                    >
                      <Heading
                        as="h5"
                        size="sm"
                        fontWeight="700"
                        color="brand.700"
                        mb={2}
                      >
                        {feature.title}
                      </Heading>
                      <Text fontSize="sm" color="gray.700">
                        {feature.description}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>

              <Divider />

              <VStack spacing={3} align="start" w="100%">
                <Heading
                  as="h4"
                  size="sm"
                  fontWeight="700"
                  color="brand.900"
                >
                  Tech Stack
                </Heading>
                <Text fontSize="sm" color="gray.700" fontFamily="mono">
                  {data.techStack}
                </Text>
              </VStack>

              <VStack spacing={3} align="start" w="100%" bg="brand.100" p={4} borderRadius="lg">
                <Heading
                  as="h4"
                  size="sm"
                  fontWeight="700"
                  color="brand.900"
                >
                  Career Impact
                </Heading>
                <Text fontSize="sm" color="gray.700">
                  {data.impact}
                </Text>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

// -------- WHY NEEDED SECTION --------
export const CollegeProjectWhyNeeded = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.xl">
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center" mb={6}>
            <Heading as="h2" size="xl" fontWeight="800" color="brand.900">
              {data.title}
            </Heading>
            <Text fontSize="lg" color="gray.700" maxW="3xl">
              {data.description}
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
            {data.reasons.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (idx % 2) * 0.1 }}
              >
                <Box
                  p={6}
                  bg="brand.50"
                  borderRadius="lg"
                  borderLeft="4px solid"
                  borderLeftColor="brand.500"
                  _hover={{
                    bg: 'brand.100',
                    transform: 'translateX(4px)',
                  }}
                  transition="all 0.3s ease"
                >
                  <VStack spacing={3} align="start">
                    <Heading
                      as="h3"
                      size="md"
                      fontWeight="700"
                      color="brand.900"
                    >
                      {item.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                      {item.reason}
                    </Text>
                  </VStack>
                </Box>
              </motion.div>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

// -------- CTA SECTION --------
export const CollegeProjectCTA = ({ data, onEnrollClick, onTalkToAdvisorClick }) => {
  return (
    <Box
      py={{ base: 20, md: 24 }}
      bgGradient="linear(to-r, brand.500, brand.600)"
      color="white"
      textAlign="center"
    >
      <Container maxW="container.lg">
        <VStack spacing={6}>
          <Heading as="h2" size="xl" fontWeight="800">
            {data.title}
          </Heading>
          <Text fontSize="lg" maxW="2xl" opacity={0.95}>
            {data.description}
          </Text>

          <HStack spacing={4} justify="center" flexWrap="wrap" pt={4}>
            {data.buttons.map((button, idx) => (
              <Button
                key={idx}
                as="a"
                href={button.href}
                size="lg"
                colorScheme={button.variant === 'solid' ? 'white' : undefined}
                variant={button.variant}
                fontWeight="700"
                px={8}
                onClick={
                  button.href === '/contact'
                    ? onTalkToAdvisorClick
                    : onEnrollClick
                }
                _hover={{ transform: 'translateY(-2px)' }}
                transition="all 0.3s ease"
              >
                {button.text}
              </Button>
            ))}
          </HStack>

          <Text fontSize="sm" opacity={0.9} pt={4}>
            {data.footer}
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};
